import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Copy, Mail, Phone } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { RootState, useAppDispatch } from '../store';
import {
  connectWhatsApp,
  createTelegramLink,
  disconnectTelegram,
  disconnectWhatsApp,
  fetchTelegramStatus,
  fetchWhatsAppStatus,
  sendTestEmail,
  sendTestTelegram,
  sendTestWhatsApp,
  verifyWhatsApp,
} from '../store/thunks/alertsThunks';
import { TelegramAlertStatus, WhatsAppAlertStatus } from '../types';
import { useAuth } from '../hooks/useAuth';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { Card, CardDescription, CardTitle } from './ui/Card';
import { Input } from './ui/Input';
import { Label } from './ui/Label';

interface AlertsConnectionsTabProps {
  onTestSuccess?: () => void;
  onChannelStatusChange?: (status: { telegramConnected: boolean; whatsappConnected: boolean }) => void;
  /** P2-2: whether the user has acknowledged Email as their delivery channel
   *  for QuickStart step 1 purposes. Email is auto-bound but per plan we
   *  don't auto-tick step 1 on that alone. */
  emailAcknowledged?: boolean;
  /** P2-2: callback to flip the email-ack flag in parent state (and persist
   *  to localStorage). */
  onAcknowledgeEmail?: () => void;
}

const formatSandboxNumber = (value?: string): string => {
  const number = (value || 'whatsapp:+14155238886').replace(/^whatsapp:/, '');
  if (number === '+14155238886') {
    return '+1 415 523 8886';
  }
  return number;
};

const copyText = async (text: string) => {
  if (!navigator.clipboard) {
    return;
  }
  await navigator.clipboard.writeText(text);
};

// CopyButton uses the secondary `Button` primitive at the smallest size.
// Compact icon-control (36px) is on the touch-target exception list per plan.
const CopyButton: React.FC<{ label: string; text: string; onCopied: () => void }> = ({ label, text, onCopied }) => (
  <Button
    variant="secondary"
    size="sm"
    aria-label={label}
    leftIcon={<Copy size={14} aria-hidden="true" />}
    onClick={async () => {
      await copyText(text);
      onCopied();
    }}
  >
    {label}
  </Button>
);

const WhatsAppSandboxCallout: React.FC<{ status: WhatsAppAlertStatus | null }> = ({ status }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<string | null>(null);

  if (!status?.configured || !status?.is_sandbox || status?.connected) {
    return null;
  }

  const sandboxNumber = formatSandboxNumber(status.whatsapp_from);
  const copyNumber = (status.whatsapp_from || 'whatsapp:+14155238886').replace(/^whatsapp:/, '');
  const keyword = status.sandbox_keyword?.trim() || '';
  const joinMessage = keyword ? `join ${keyword}` : '';

  const markCopied = (key: string) => {
    setCopied(key);
    window.setTimeout(() => setCopied(null), 1600);
  };

  if (!keyword) {
    return (
      <div
        className="mb-4 rounded-lg border border-accent-warning/40 bg-accent-warning/10 p-3 text-sm text-text-primary"
        role="alert"
      >
        {t('alerts.connections.whatsapp.sandbox.missingKeyword')}
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-lg bg-surface-container-low p-4">
      <h4 className="text-sm font-semibold text-text-primary">
        {t('alerts.connections.whatsapp.sandbox.title')}
      </h4>
      <p className="mt-1 text-sm text-text-muted">{t('alerts.connections.whatsapp.sandbox.intro')}</p>
      <ol className="mt-3 space-y-2 text-sm text-text-primary">
        <li>{t('alerts.connections.whatsapp.sandbox.step1')}</li>
        <li className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span>
            {t('alerts.connections.whatsapp.sandbox.step2')}{' '}
            <span className="font-mono text-text-primary">{sandboxNumber}</span>
          </span>
          <CopyButton
            label={
              copied === 'number'
                ? t('alerts.connections.whatsapp.sandbox.copied')
                : t('alerts.connections.whatsapp.sandbox.copyNumber')
            }
            text={copyNumber}
            onCopied={() => markCopied('number')}
          />
        </li>
        <li className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span>
            {t('alerts.connections.whatsapp.sandbox.step3')}{' '}
            <span className="font-mono text-text-primary">{joinMessage}</span>
          </span>
          <CopyButton
            label={
              copied === 'join'
                ? t('alerts.connections.whatsapp.sandbox.copied')
                : t('alerts.connections.whatsapp.sandbox.copyJoin')
            }
            text={joinMessage}
            onCopied={() => markCopied('join')}
          />
        </li>
        <li>{t('alerts.connections.whatsapp.sandbox.step4')}</li>
        <li>{t('alerts.connections.whatsapp.sandbox.step5')}</li>
      </ol>
      <p className="mt-3 text-xs text-text-muted">{t('alerts.connections.whatsapp.sandbox.sessionNote')}</p>
    </div>
  );
};

interface ChannelCardProps {
  title: string;
  subtitle?: string | undefined;
  statusBadge?: React.ReactNode | undefined;
  children: React.ReactNode;
}

// Channel card: borderless layered surface (separation by surface contrast, not
// an outline). Title row is inline (no CardHeader - its negative margins +
// tinted band would conflict with our flat look). Hover lifts the surface a step
// for a subtle interactive cue without movement.
const ChannelCard: React.FC<ChannelCardProps> = ({ title, subtitle, statusBadge, children }) => (
  <Card variant="ghost" className="bg-surface-container-high transition-colors hover:bg-surface-container-highest">
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <CardTitle as="h3" className="text-base font-semibold text-text-primary">
        {title}
      </CardTitle>
      {statusBadge}
    </div>
    {subtitle && <CardDescription className="mb-3">{subtitle}</CardDescription>}
    {children}
  </Card>
);

const AlertsConnectionsTab: React.FC<AlertsConnectionsTabProps> = ({
  onTestSuccess,
  onChannelStatusChange,
  emailAcknowledged = false,
  onAcknowledgeEmail,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const { loading, whatsappStatus } = useSelector(
    (state: RootState) => state.alerts || { loading: false, whatsappStatus: null }
  );

  const [testEmail, setTestEmail] = useState(user?.email || '');
  const [testEmailSent, setTestEmailSent] = useState(false);
  const [testEmailError, setTestEmailError] = useState<string | null>(null);
  const [telegramStatus, setTelegramStatus] = useState<TelegramAlertStatus | null>(null);
  const [telegramConnectUrl, setTelegramConnectUrl] = useState<string | null>(null);
  const [telegramConnectBusy, setTelegramConnectBusy] = useState(false);
  const [telegramTestSent, setTelegramTestSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [whatsappNotice, setWhatsappNotice] = useState<string | null>(null);
  const [whatsappError, setWhatsappError] = useState<string | null>(null);

  const refreshTelegramStatus = useCallback(async () => {
    try {
      const status = await dispatch(fetchTelegramStatus() as any);
      setTelegramStatus(status);
      return status as TelegramAlertStatus;
    } catch {
      setTelegramStatus(null);
      return null;
    }
  }, [dispatch]);

  useEffect(() => {
    refreshTelegramStatus();
    dispatch(fetchWhatsAppStatus() as any).catch(() => undefined);
  }, [dispatch, refreshTelegramStatus]);

  useEffect(() => {
    onChannelStatusChange?.({
      telegramConnected: Boolean(telegramStatus?.connected),
      whatsappConnected: Boolean(whatsappStatus?.connected),
    });
  }, [onChannelStatusChange, telegramStatus?.connected, whatsappStatus?.connected]);

  const whatsappMissingKeyword = Boolean(
    whatsappStatus?.configured && whatsappStatus?.is_sandbox && !whatsappStatus?.sandbox_keyword?.trim()
  );

  const whatsappConnectedText = useMemo(() => {
    const phone = whatsappStatus?.binding?.phone_number;
    return phone
      ? t('alerts.connections.whatsapp.connected', { phone })
      : t('alerts.connections.whatsapp.connected', { phone: '' }).trim();
  }, [t, whatsappStatus?.binding?.phone_number]);

  const handleSendTestEmail = async () => {
    setTestEmailError(null);
    if (!testEmail) {
      setTestEmailError('Please enter an email address.');
      return;
    }
    try {
      await dispatch(sendTestEmail(testEmail));
      setTestEmailSent(true);
      onTestSuccess?.();
      setTimeout(() => setTestEmailSent(false), 3000);
    } catch (err: any) {
      setTestEmailError(err?.message || 'Failed to send test email.');
    }
  };

  const handleCreateTelegramLink = async () => {
    setTelegramConnectBusy(true);
    try {
      const result = await dispatch(createTelegramLink() as any);
      setTelegramConnectUrl(result?.connect_url || null);
      await refreshTelegramStatus();
    } finally {
      setTelegramConnectBusy(false);
    }
  };

  const handleSendTestTelegram = async () => {
    try {
      await dispatch(sendTestTelegram() as any);
      setTelegramTestSent(true);
      onTestSuccess?.();
      setTimeout(() => setTelegramTestSent(false), 3000);
    } catch {
      // Shared alerts error banner is populated by the thunk.
    }
  };

  const handleDisconnectTelegram = async () => {
    await dispatch(disconnectTelegram() as any);
    setTelegramConnectUrl(null);
    await refreshTelegramStatus();
  };

  const handleConnectWhatsApp = async () => {
    setWhatsappError(null);
    setWhatsappNotice(null);
    if (!phoneNumber.trim()) {
      setWhatsappError('Enter your WhatsApp phone number.');
      return;
    }
    try {
      const result = await dispatch(connectWhatsApp(phoneNumber.trim()) as any);
      setWhatsappNotice(result?.message || 'Verification code sent.');
      await dispatch(fetchWhatsAppStatus() as any);
    } catch (err: any) {
      setWhatsappError(err?.message || 'Failed to send verification code.');
    }
  };

  const handleVerifyWhatsApp = async () => {
    setWhatsappError(null);
    setWhatsappNotice(null);
    if (!verificationCode.trim()) {
      setWhatsappError('Enter the verification code.');
      return;
    }
    try {
      const result = await dispatch(verifyWhatsApp(verificationCode.trim()) as any);
      setWhatsappNotice(result?.message || 'WhatsApp verified.');
      await dispatch(fetchWhatsAppStatus() as any);
    } catch (err: any) {
      setWhatsappError(err?.message || 'Failed to verify WhatsApp.');
    }
  };

  const handleSendTestWhatsApp = async () => {
    setWhatsappError(null);
    setWhatsappNotice(null);
    try {
      const result = await dispatch(sendTestWhatsApp() as any);
      setWhatsappNotice(result?.message || 'Test WhatsApp message sent.');
      onTestSuccess?.();
    } catch (err: any) {
      setWhatsappError(err?.message || 'Failed to send WhatsApp test message.');
    }
  };

  const handleDisconnectWhatsApp = async () => {
    await dispatch(disconnectWhatsApp() as any);
    await dispatch(fetchWhatsAppStatus() as any).catch(() => undefined);
  };

  // Per-channel status badges (Phase G):
  // - Email: purely informational (account email), no connection state
  // - Telegram / WhatsApp: bullish/default with showDot for connection state
  // P3-1: don't render the email-as-badge - the email is already shown in the
  // input field below, and duplicating it added visual noise. When the user
  // has acknowledged email as their channel (P2-2), show a green "Acknowledged"
  // badge instead so the state is visible.
  const emailStatusBadge = emailAcknowledged ? (
    <Badge variant="bullish" size="sm" showDot>
      {t('alerts.connections.email.acknowledged')}
    </Badge>
  ) : undefined;

  const telegramStatusBadge = (
    <Badge variant={telegramStatus?.connected ? 'bullish' : 'default'} size="sm" showDot>
      {telegramStatus?.connected
        ? t('alerts.connections.telegram.connected')
        : t('alerts.connections.telegram.notConnected')}
    </Badge>
  );

  // Use a short, badge-appropriate "Connected" string. The longer
  // "Connected to {{phone}}" copy is reserved for the subtitle/header where
  // there is room to render the phone number.
  const whatsappStatusBadge = (
    <Badge variant={whatsappStatus?.connected ? 'bullish' : 'default'} size="sm" showDot>
      {whatsappStatus?.connected
        ? t('alerts.connections.whatsapp.statusConnected')
        : t('alerts.connections.whatsapp.notConnected')}
    </Badge>
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-text-primary">{t('alerts.connections.title')}</h2>
        <p className="mt-1 text-sm text-text-muted">{t('alerts.connections.subtitle')}</p>
      </div>

      <ChannelCard title={t('alerts.connections.email.title')} statusBadge={emailStatusBadge}>
        <div className="flex flex-col gap-2 md:flex-row">
          <Label htmlFor="alerts-email-input" className="sr-only">
            {t('alerts.connections.email.title')}
          </Label>
          <div className="flex-1">
            <Input
              id="alerts-email-input"
              type="email"
              leftIcon={<Mail size={16} aria-hidden="true" />}
              placeholder="your@email.com"
              value={testEmail}
              onChange={(e) => {
                setTestEmail(e.target.value);
                if (testEmailError) setTestEmailError(null);
              }}
            />
          </div>
          <Button
            variant="primary"
            size="md"
            onClick={handleSendTestEmail}
            disabled={loading || !testEmail}
          >
            {testEmailSent ? t('alerts.connections.email.tested') : t('alerts.connections.email.test')}
          </Button>
        </div>
        {/* P2-2: explicit "use email" acknowledgment so QuickStart step 1 can
            complete for users whose delivery is email-only. Hidden once
            acknowledged. */}
        {!emailAcknowledged && onAcknowledgeEmail && (
          <div className="mt-3">
            <Button variant="ghost" size="sm" className="min-h-[44px]" onClick={onAcknowledgeEmail}>
              {t('alerts.connections.email.useForAlerts')}
            </Button>
          </div>
        )}
        {testEmailError && (
          <div
            className="mt-2 rounded-lg border border-accent-bearish/40 bg-accent-bearish/10 p-2 text-sm text-accent-bearish"
            role="alert"
          >
            {testEmailError}
          </div>
        )}
      </ChannelCard>

      <ChannelCard
        title={t('alerts.connections.telegram.title')}
        subtitle={
          telegramStatus?.configured
            ? telegramStatus?.connected
              ? telegramStatus.binding?.telegram_username
                ? `@${telegramStatus.binding.telegram_username}`
                : undefined
              : undefined
            : 'Telegram bot is not configured on the server'
        }
        statusBadge={telegramStatusBadge}
      >
        {telegramConnectUrl && (
          <a
            href={telegramConnectUrl}
            target="_blank"
            rel="noreferrer"
            className="mb-3 block break-all text-sm text-accent-primary underline"
          >
            {telegramConnectUrl}
          </a>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            size="md"
            onClick={handleCreateTelegramLink}
            disabled={loading || telegramConnectBusy || !telegramStatus?.configured}
          >
            {telegramConnectBusy ? 'Creating...' : t('alerts.connections.telegram.connect')}
          </Button>
          <Button
            variant="secondary"
            size="md"
            onClick={handleSendTestTelegram}
            disabled={loading || !telegramStatus?.connected}
          >
            {telegramTestSent ? t('alerts.connections.email.tested') : t('alerts.connections.telegram.test')}
          </Button>
          <Button
            variant="ghost"
            size="md"
            className="text-accent-bearish hover:bg-accent-bearish/10 hover:text-accent-bearish"
            onClick={handleDisconnectTelegram}
            disabled={loading || !telegramStatus?.connected}
          >
            {t('alerts.connections.telegram.disconnect')}
          </Button>
        </div>
      </ChannelCard>

      <ChannelCard
        title={t('alerts.connections.whatsapp.title')}
        subtitle={
          whatsappStatus?.configured
            ? whatsappStatus?.connected
              ? whatsappConnectedText
              : undefined
            : 'WhatsApp is not configured on the server'
        }
        statusBadge={whatsappStatusBadge}
      >
        <WhatsAppSandboxCallout status={whatsappStatus} />

        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
          <div>
            <Label htmlFor="alerts-whatsapp-phone" className="mb-1 block font-medium text-text-primary">
              {t('alerts.connections.whatsapp.phoneLabel')}
            </Label>
            <Input
              id="alerts-whatsapp-phone"
              type="tel"
              leftIcon={<Phone size={16} aria-hidden="true" />}
              placeholder={t('alerts.connections.whatsapp.phonePlaceholder')}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </div>
          <Button
            variant="primary"
            size="md"
            className="self-end"
            onClick={handleConnectWhatsApp}
            disabled={loading || !whatsappStatus?.configured || whatsappMissingKeyword}
            title={
              whatsappMissingKeyword ? t('alerts.connections.whatsapp.sandbox.missingKeyword') : undefined
            }
          >
            {t('alerts.connections.whatsapp.sendCode')}
          </Button>
        </div>

        <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
          <div>
            <Label htmlFor="alerts-whatsapp-code" className="mb-1 block font-medium text-text-primary">
              {t('alerts.connections.whatsapp.codeLabel')}
            </Label>
            <Input
              id="alerts-whatsapp-code"
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
            />
          </div>
          <Button
            variant="secondary"
            size="md"
            className="self-end"
            onClick={handleVerifyWhatsApp}
            disabled={loading || !verificationCode}
          >
            {t('alerts.connections.whatsapp.verify')}
          </Button>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="md"
            onClick={handleSendTestWhatsApp}
            disabled={loading || !whatsappStatus?.connected}
          >
            {t('alerts.connections.whatsapp.test')}
          </Button>
          <Button
            variant="ghost"
            size="md"
            className="text-accent-bearish hover:bg-accent-bearish/10 hover:text-accent-bearish"
            onClick={handleDisconnectWhatsApp}
            disabled={loading || !whatsappStatus?.connected}
          >
            {t('alerts.connections.whatsapp.disconnect')}
          </Button>
        </div>

        {whatsappNotice && (
          <div
            className="mt-3 rounded-lg border border-accent-bullish/40 bg-accent-bullish/10 p-2 text-sm text-accent-bullish"
            role="status"
          >
            {whatsappNotice}
          </div>
        )}
        {whatsappError && (
          <div
            className="mt-3 rounded-lg border border-accent-bearish/40 bg-accent-bearish/10 p-2 text-sm text-accent-bearish"
            role="alert"
          >
            {whatsappError}
          </div>
        )}
      </ChannelCard>
    </div>
  );
};

export default AlertsConnectionsTab;
